/*
 * File: src/components/OutstandingOrders/OutstandingOrdersRow.jsx
 * Purpose: Renders a single Outstanding Order row and its shipping status widget.
 */

import React from 'react';
import PropTypes from 'prop-types';

import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import TableDate from '../TableDate.jsx';
import OutstandingOrderShippingStatus from './OutstandingOrderShippingStatus.jsx';

function getFormattedOrderType(orderType) {
  const code = String(orderType || '')
    .trim()
    .toUpperCase();

  if (code === 'B') {
    return 'Backordered or Made to Order';
  }

  if (code === 'S') {
    return 'Processing';
  }

  if (code === 'Q') {
    return 'Rep Action Required';
  }

  if (!code) {
    return '';
  }

  return `Unknown Type (${code})`;
}

function OutstandingOrdersRow({ order }) {
  const reps = React.useMemo(() => [order.Rep1, order.Rep2, order.Rep3].filter(Boolean), [order]);
  const formattedOrderType = React.useMemo(
    () => order.FormattedOrderType || getFormattedOrderType(order.OrderType),
    [order.FormattedOrderType, order.OrderType]
  );

  return (
    <TableRow>
      <TableCell data-cy={`sales-order-no-${order.SalesOrderNo}`}>{order.SalesOrderNo}</TableCell>
      <TableCell data-cy={`order-date-${order.SalesOrderNo}`}>
        <TableDate date={order.OrderDate} />
      </TableCell>
      <TableCell data-cy={`ship-to-name-${order.SalesOrderNo}`}>{order.ShipToName}</TableCell>
      <TableCell data-cy={`customer-po-no-${order.SalesOrderNo}`}>{order.CustomerPONo}</TableCell>
      <TableCell data-cy={`order-type-${order.SalesOrderNo}`}>{formattedOrderType}</TableCell>
      <TableCell data-cy={`ship-via-${order.SalesOrderNo}`}>{order.ShipVia}</TableCell>
      <TableCell data-cy={`reps-${order.SalesOrderNo}`} sx={{ minWidth: 100 }}>
        <Grid container spacing={1}>
          {reps.map((rep) => (
            <Grid size="auto" key={rep}>
              <Chip label={rep} data-cy={`rep-${rep}-${order.SalesOrderNo}`} />
            </Grid>
          ))}
        </Grid>
      </TableCell>
      <TableCell data-cy={`shipping-status-${order.SalesOrderNo}`}>
        <OutstandingOrderShippingStatus
          shipments={order.shipments || []}
          summary={order.shippingSummary}
        />
      </TableCell>
      <TableCell data-cy={`item-code-${order.SalesOrderNo}`}>{order.ItemCode}</TableCell>
      <TableCell data-cy={`qty-${order.SalesOrderNo}`}>{order.Qty}</TableCell>
      <TableCell data-cy={`purchase-order-no-${order.SalesOrderNo}`}>
        {order.PurchaseOrderNo}
      </TableCell>
      <TableCell data-cy={`purchase-order-date-${order.SalesOrderNo}`}>
        {order.PurchaseOrderDate}
      </TableCell>
    </TableRow>
  );
}

OutstandingOrdersRow.propTypes = {
  order: PropTypes.shape({
    SalesOrderNo: PropTypes.string.isRequired,
    OrderDate: PropTypes.string.isRequired,
    ShipToName: PropTypes.string.isRequired,
    CustomerPONo: PropTypes.string,
    PurchaseOrderNo: PropTypes.string,
    PurchaseOrderDate: PropTypes.string,
    OrderType: PropTypes.string.isRequired,
    FormattedOrderType: PropTypes.string,
    ShipVia: PropTypes.string.isRequired,
    Rep1: PropTypes.string,
    Rep2: PropTypes.string,
    Rep3: PropTypes.string,
    ItemCode: PropTypes.string.isRequired,
    Qty: PropTypes.string.isRequired,
    total: PropTypes.number,
    shippingSummary: PropTypes.shape({
      orderedQty: PropTypes.number,
      receivedQty: PropTypes.number,
      readyToInvoice: PropTypes.bool,
      status: PropTypes.string,
    }),
    shipments: PropTypes.arrayOf(
      PropTypes.shape({
        ID: PropTypes.string.isRequired,
        date_received: PropTypes.string,
        Tracking_No: PropTypes.string,
      })
    ),
  }).isRequired,
};

export default React.memo(OutstandingOrdersRow);
